import initCompanyNumberInputs from './companies-house';
import { destroySearchSelects, initSearchSelects } from './search-select';
import { initSelectFieldTargets, syncSelectFieldTargets } from './select-targets';

const resetSearchSelectFields = (root) => {
    root.querySelectorAll('.js-form-search-select').forEach((field) => {
        field.querySelector('.js-form-search-select-autocomplete')?.replaceChildren();
        field.querySelector('.js-form-search-select-tags')?.replaceChildren();
        field.querySelector('.js-form-search-select-tags')?.classList.add('hidden');
        field.querySelector('.js-form-search-select-other')?.classList.add('hidden');

        const otherInput = field.querySelector('.js-form-search-select-other-input');

        if (otherInput) {
            otherInput.value = '';
        }

        const hiddenInput = field.querySelector('.js-form-search-select-value');

        if (hiddenInput) {
            hiddenInput.value = '';
        }
    });
};

const updateSectionLabels = (group) => {
    group.querySelectorAll('.js-form-field-group-batch').forEach((batch, batchIndex) => {
        batch.querySelectorAll('.js-form-field-group-section-label').forEach((label) => {
            const baseLabel = label.dataset.baseLabel || label.textContent.replace(/\s*\(\d+\)$/, '').trim();

            label.dataset.baseLabel = baseLabel;
            label.textContent = batchIndex === 0 ? baseLabel : `${baseLabel} (${batchIndex + 1})`;
        });
    });
};

const updateBatchFieldMeta = (batch, batchIndex, group) => {
    const { formId, groupIndex } = group.dataset;

    batch.dataset.batchIndex = String(batchIndex);

    batch.querySelectorAll('.js-form-input').forEach((input) => {
        const { fieldIndex } = input.dataset;

        if (fieldIndex !== undefined) {
            const fieldId = `field-${formId}-${groupIndex}-${batchIndex}-${fieldIndex}`;
            const searchSelect = input.closest('.js-form-search-select');
            input.id = fieldId;
            input.name = fieldId;

            const wrapper = input.parentElement;
            const labelFor = wrapper?.tagName === 'LABEL'
                ? wrapper
                : wrapper?.querySelector('label');

            if (labelFor) {
                labelFor.setAttribute('for', searchSelect ? `${fieldId}-search` : fieldId);
            }
        }

        const baseKey = input.dataset.baseKey || input.dataset.key.replace(/__\d+$/, '');
        const baseLabel = input.dataset.baseLabel || input.dataset.label.replace(/\s*\(\d+\)$/, '').trim();

        input.dataset.baseKey = baseKey;
        input.dataset.baseLabel = baseLabel;

        input.dataset.baseKey = baseKey;

        if (batchIndex === 0) {
            input.dataset.key = baseKey;
            input.dataset.label = baseLabel;
        } else {
            input.dataset.key = `${baseKey}__${batchIndex}`;
            // input.dataset.label = `${baseLabel} (${batchIndex + 1})`;
        }

        const requiredMarkup = input.classList.contains('is-required') ? '<sup class="text-error">*</sup>' : '';
        const labelText = `${input.dataset.label}${requiredMarkup}`;
        const checkboxLabel = input.parentElement?.querySelector('.js-form-field-group-checkbox-label');

        if (checkboxLabel) {
            checkboxLabel.innerHTML = labelText;
            return;
        }

        const wrapper = input.parentElement;
        const searchSelectField = input.closest('.js-form-search-select');
        const label = searchSelectField?.querySelector('label')
            ?? (wrapper?.tagName === 'LABEL' ? null : wrapper?.querySelector('label'));

        if (label) {
            label.innerHTML = labelText;
        }
    });
};

const reindexBatches = (group) => {
    const batches = group.querySelectorAll('.js-form-field-group-batch');
    const minBatches = Number(group.dataset.minBatches || 1);
    const maxBatches = Number(group.dataset.maxBatches || 0);

    batches.forEach((batch, batchIndex) => {
        updateBatchFieldMeta(batch, batchIndex, group);

        const removeBtn = batch.querySelector('.js-form-field-group-remove');

        if (removeBtn) {
            removeBtn.classList.toggle('hidden', batches.length <= minBatches);
        }
    });

    updateSectionLabels(group);

    const addBtn = group.querySelector('.js-form-field-group-add');

    if (addBtn) {
        addBtn.classList.toggle('hidden', maxBatches > 0 && batches.length >= maxBatches);
    }
};

const cloneBatch = (group) => {
    const batches = group.querySelector('.js-form-field-group-batches');
    const template = group.querySelector('.js-form-field-group-template');
    const source = template?.content?.firstElementChild;

    if (!batches || !source) return null;

    const clone = source.cloneNode(true);

    clone.querySelectorAll('.js-form-input').forEach((input) => {
        switch (input.type) {
            case 'checkbox':
                input.checked = false;
                break;
            case 'select-one':
                input.selectedIndex = 0;
                input.value = '';
                break;
            default:
                input.value = '';
                break;
        }

        input.parentNode.classList.remove('has-error');
        delete input.dataset.companyLookupInit;
        delete input.dataset.selectTargetInit;
    });

    clone.querySelectorAll('.js-form-select-target').forEach((field) => {
        field.classList.add('hidden');
    });

    clone.querySelectorAll('.js-form-company-number-status').forEach((status) => {
        status.textContent = '';
        status.classList.add('hidden');
    });

    resetSearchSelectFields(clone);

    batches.appendChild(clone);
    reindexBatches(group);
    initCompanyNumberInputs(clone);
    initSearchSelects(clone);

    const form = group.closest('.js-form');

    if (form) {
        initSelectFieldTargets(form);
        syncSelectFieldTargets(form);
    }

    return clone;
};

const resetFieldGroups = (form) => {
    form.querySelectorAll('.js-form-field-group').forEach((group) => {
        const batches = group.querySelectorAll('.js-form-field-group-batch');
        const minBatches = Number(group.dataset.minBatches || 1);

        batches.forEach((batch, index) => {
            if (index >= minBatches) {
                batch.remove();
            } else {
                batch.querySelectorAll('.js-form-input').forEach((input) => {
                    switch (input.type) {
                        case 'checkbox':
                            input.checked = false;
                            break;
                        default:
                            input.value = '';
                            break;
                    }

                    input.parentNode.classList.remove('has-error');
                });

                batch.querySelectorAll('.js-form-company-number-status').forEach((status) => {
                    status.textContent = '';
                    status.classList.add('hidden');
                });
            }
        });

        reindexBatches(group);
    });
};

const initFieldGroups = (form) => {
    form.querySelectorAll('.js-form-field-group').forEach((group) => {
        if (group.dataset.fieldGroupInit) return;

        group.dataset.fieldGroupInit = 'true';

        // Render the initial batches from the template (min_batches; 0 => none,
        // just the "Add company" button).
        const initialBatches = Number(group.dataset.minBatches || 0);
        const existing = group.querySelectorAll('.js-form-field-group-batch').length;

        for (let i = existing; i < initialBatches; i += 1) {
            cloneBatch(group);
        }

        reindexBatches(group);

        group.addEventListener('click', (event) => {
            const addBtn = event.target.closest('.js-form-field-group-add');
            const removeBtn = event.target.closest('.js-form-field-group-remove');

            if (addBtn) {
                event.preventDefault();
                cloneBatch(group);
                return;
            }

            if (removeBtn) {
                event.preventDefault();
                const batch = removeBtn.closest('.js-form-field-group-batch');
                const batches = group.querySelectorAll('.js-form-field-group-batch');
                const minBatches = Number(group.dataset.minBatches || 1);

                if (batch && batches.length > minBatches) {
                    destroySearchSelects(batch);
                    batch.remove();
                    reindexBatches(group);
                }
            }
        });
    });
};

export { initFieldGroups, resetFieldGroups };
